import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Footer from "@/components/Footer";
import LandingNav from "@/components/LandingNav";
import { AppHeader } from "@/components/AppHeader";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import SEOHead from "@/components/SEOHead";
import { Rocket, Package, Users, Code, Shield, Monitor, LifeBuoy, FileDown, Bot } from "lucide-react";

const Help = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  return (
    <SidebarProvider defaultOpen={false}>
      <div className="min-h-screen bg-background flex flex-col w-full">
        <SEOHead
          title="Help Center – TestAssets"
          description="Get help with TestAssets. FAQs, guides for browser extensions, desktop apps, API usage, and team collaboration."
          path="/help"
          breadcrumbs={[
            { name: "Home", url: "/" },
            { name: "Help Center", url: "/help" },
          ]}
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              { "@type": "Question", name: "How do I create an asset list?", acceptedAnswer: { "@type": "Answer", text: "After logging in, click the \"+\" button next to \"Asset Lists\" in the sidebar. Give your list a title and an optional description, then click \"Create\"." } },
              { "@type": "Question", name: "How do I add assets to a list?", acceptedAnswer: { "@type": "Answer", text: "Open an asset list from the sidebar, then click the \"+\" button above the asset table. Enter a name for your asset and click \"Add\"." } },
              { "@type": "Question", name: "What are nested (sub-)assets?", acceptedAnswer: { "@type": "Answer", text: "Assets can have sub-assets to represent hierarchical structures. When viewing an asset's details, you can add child assets underneath it." } },
              { "@type": "Question", name: "How do I import or export assets?", acceptedAnswer: { "@type": "Answer", text: "Open an asset list and use the import/export buttons above the asset table. You can import assets from CSV, JSON, or XLSX files." } },
              { "@type": "Question", name: "How do I share an asset list with team members?", acceptedAnswer: { "@type": "Answer", text: "Go to the Share page, select an asset list, enter a team member's email address, choose a permission level, and send the invite." } },
              { "@type": "Question", name: "How do I get an API key?", acceptedAnswer: { "@type": "Answer", text: "Go to Settings, in the API Keys section click \"Generate\". Copy the key immediately — it will only be shown once." } },
              { "@type": "Question", name: "Is TestAssets free?", acceptedAnswer: { "@type": "Answer", text: "TestAssets is currently free during the pilot phase. All features are available at no cost." } },
              { "@type": "Question", name: "Which platforms is TestAssets available on?", acceptedAnswer: { "@type": "Answer", text: "TestAssets is available as a web application, desktop app for Windows and macOS, browser extensions, and via a REST API." } },
              { "@type": "Question", name: "What is the MCP server?", acceptedAnswer: { "@type": "Answer", text: "The MCP (Model Context Protocol) server allows AI agents and coding assistants to read your TestAssets data directly, exposing asset lists and assets as structured tools." } },
              { "@type": "Question", name: "How do I connect my AI assistant to TestAssets via MCP?", acceptedAnswer: { "@type": "Answer", text: "Add a new MCP server entry in your AI tool's configuration, set the URL to the TestAssets MCP endpoint, and provide your API key in the header." } },
              { "@type": "Question", name: "How do I fill TestAssets values into other apps on Android?", acceptedAnswer: { "@type": "Answer", text: "Open the TestAssets mobile app, go to Quick Access → Set up Quick Fill, and pick TestAssets as your Android autofill service. After that, tapping any text field in any app will show a TestAssets suggestion above the keyboard. Tap it to open the in-app picker, choose the value you want, and it will be inserted into the field." } },
              { "@type": "Question", name: "Can I auto-fill TestAssets values on iOS?", acceptedAnswer: { "@type": "Answer", text: "Apple does not allow third-party apps to type into other apps for arbitrary values. Use the in-app Quick Access screen to copy a value, then long-press the field in the other app and choose Paste." } },
            ],
          }}
        />
        {user ? <AppHeader /> : <LandingNav />}

      <main className="flex-1 container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold text-foreground">{t("help.title")}</h1>
            <p className="text-xl text-muted-foreground">
              {t("help.description")}
            </p>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <Rocket className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle>{t("help.gettingStarted.title")}</CardTitle>
                  <CardDescription>{t("help.gettingStarted.description")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1">
                  <AccordionTrigger>{t("help.gettingStarted.createList.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.gettingStarted.createList.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger>{t("help.gettingStarted.createAsset.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.gettingStarted.createAsset.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3">
                  <AccordionTrigger>{t("help.gettingStarted.nestedAssets.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.gettingStarted.nestedAssets.answer")}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <Package className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle>{t("help.assetManagement.title")}</CardTitle>
                  <CardDescription>{t("help.assetManagement.description")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="mgmt-1">
                  <AccordionTrigger>{t("help.assetManagement.importExport.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.assetManagement.importExport.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="mgmt-2">
                  <AccordionTrigger>{t("help.assetManagement.searchFilter.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.assetManagement.searchFilter.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="mgmt-3">
                  <AccordionTrigger>{t("help.assetManagement.allAssets.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.assetManagement.allAssets.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="mgmt-4">
                  <AccordionTrigger>{t("help.assetManagement.bulkPaste.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.assetManagement.bulkPaste.answer")}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <FileDown className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle>{t("help.importExportDetails.title")}</CardTitle>
                  <CardDescription>{t("help.importExportDetails.description")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="ie-1">
                  <AccordionTrigger>{t("help.importExportDetails.supportedFormats.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.importExportDetails.supportedFormats.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="ie-2">
                  <AccordionTrigger>{t("help.importExportDetails.formatRequirements.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.importExportDetails.formatRequirements.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="ie-3">
                  <AccordionTrigger>{t("help.importExportDetails.hierarchicalData.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.importExportDetails.hierarchicalData.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="ie-4">
                  <AccordionTrigger>{t("help.importExportDetails.exportSharedLists.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.importExportDetails.exportSharedLists.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="ie-5">
                  <AccordionTrigger>{t("help.importExportDetails.importLimitations.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.importExportDetails.importLimitations.answer")}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <Users className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle>{t("help.sharingCollaboration.title")}</CardTitle>
                  <CardDescription>{t("help.sharingCollaboration.description")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="share-1">
                  <AccordionTrigger>{t("help.sharingCollaboration.inviteMembers.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.sharingCollaboration.inviteMembers.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="share-2">
                  <AccordionTrigger>{t("help.sharingCollaboration.roles.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.sharingCollaboration.roles.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="share-3">
                  <AccordionTrigger>{t("help.sharingCollaboration.publicLinks.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.sharingCollaboration.publicLinks.answer")}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <Code className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle>{t("help.apiIntegrations.title")}</CardTitle>
                  <CardDescription>{t("help.apiIntegrations.description")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="api-1">
                  <AccordionTrigger>{t("help.apiIntegrations.getApiKey.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.apiIntegrations.getApiKey.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="api-2">
                  <AccordionTrigger>{t("help.apiIntegrations.apiDocs.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.apiIntegrations.apiDocs.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="api-3">
                  <AccordionTrigger>{t("help.apiIntegrations.rateLimits.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.apiIntegrations.rateLimits.answer")}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <Bot className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle>{t("help.mcpServer.title")}</CardTitle>
                  <CardDescription>{t("help.mcpServer.description")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="mcp-1">
                  <AccordionTrigger>{t("help.mcpServer.whatIsMcp.question")}</AccordionTrigger>
                  <AccordionContent>{t("help.mcpServer.whatIsMcp.answer")}</AccordionContent>
                </AccordionItem>
                <AccordionItem value="mcp-2">
                  <AccordionTrigger>{t("help.mcpServer.howToConnect.question")}</AccordionTrigger>
                  <AccordionContent>{t("help.mcpServer.howToConnect.answer")}</AccordionContent>
                </AccordionItem>
                <AccordionItem value="mcp-3">
                  <AccordionTrigger>{t("help.mcpServer.availableTools.question")}</AccordionTrigger>
                  <AccordionContent>{t("help.mcpServer.availableTools.answer")}</AccordionContent>
                </AccordionItem>
                <AccordionItem value="mcp-4">
                  <AccordionTrigger>{t("help.mcpServer.authentication.question")}</AccordionTrigger>
                  <AccordionContent>{t("help.mcpServer.authentication.answer")}</AccordionContent>
                </AccordionItem>
                <AccordionItem value="mcp-5">
                  <AccordionTrigger>{t("help.mcpServer.useCases.question")}</AccordionTrigger>
                  <AccordionContent>{t("help.mcpServer.useCases.answer")}</AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle>{t("help.accountSecurity.title")}</CardTitle>
                  <CardDescription>{t("help.accountSecurity.description")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="security-1">
                  <AccordionTrigger>{t("help.accountSecurity.changePassword.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.accountSecurity.changePassword.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="security-2">
                  <AccordionTrigger>{t("help.accountSecurity.dataEncrypted.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.accountSecurity.dataEncrypted.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="security-3">
                  <AccordionTrigger>{t("help.accountSecurity.deleteAccount.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.accountSecurity.deleteAccount.answer")}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <Monitor className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle>{t("help.platformAvailability.title")}</CardTitle>
                  <CardDescription>{t("help.platformAvailability.description")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="platform-1">
                  <AccordionTrigger>{t("help.platformAvailability.platforms.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.platformAvailability.platforms.answer")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="platform-2">
                  <AccordionTrigger>{t("help.platformAvailability.pricing.question")}</AccordionTrigger>
                  <AccordionContent>
                    {t("help.platformAvailability.pricing.answer")}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <LifeBuoy className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle>{t("help.contactSupport.title")}</CardTitle>
                  <CardDescription>{t("help.contactSupport.description")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                {t("help.contactSupport.message")}
              </p>
              <div className="space-y-2">
                <p className="text-sm">
                  <strong>{t("help.contactSupport.email")}</strong> <a href="mailto:support@testassets.io" className="text-primary hover:underline">support@testassets.io</a>
                </p>
                <p className="text-sm">
                  <strong>{t("help.contactSupport.responseTime")}</strong> {t("help.contactSupport.responseTimeValue")}
                </p>
              </div>
            </CardContent>
          </Card>
          <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/applications" className="text-primary hover:underline">
              {t("applications.title", "Applications & Integrations")} →
            </Link>
            <Link to="/api-docs" className="text-primary hover:underline">
              {t("footer.apiDocs", "API Docs")} →
            </Link>
            <Link to="/mcp-docs" className="text-primary hover:underline">
              {t("footer.mcpDocs", "MCP Server")} →
            </Link>
            <Link to="/pricing" className="text-primary hover:underline">
              {t("pricing.title", "Pricing")} →
            </Link>
          </div>
        </div>
      </main>

      <Footer />
      </div>
    </SidebarProvider>
  );
};

export default Help;